/* eslint-disable no-useless-escape */
import { useAuth } from "@with-nx/auth";
import {
  Formatter,
  useBusiness,
  useCMSTestimonials,
  useContact,
  useMobile,
} from "@with-nx/hooks-n-helpers";
import { useRegion, useServerRegion } from "@with-nx/region";
import {
  DesignedButton,
  DesignedIcon,
  ReusableCustomerServiceContainer,
  Rule,
} from "@with-nx/simple-ui/atoms";
import { ReusableSection } from "@with-nx/simple-ui/atoms";
import {
  CatalogLayout,
  ContactDetails,
  ContactForm,
  GridStepScroller,
  HelpDetails,
  HelpFormModal,
  ReusableDoubleGradientCard,
  ReusableDownloadCallToAction,
  ReusableGradientHero,
  ReusablePreSlantedContainer,
  TestimonialQuotes,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import { useCallback, useEffect, useState } from "react";
import Toast from "react-hot-toast";
import { FiMessageCircle } from "react-icons/fi";
import { Box } from "simple-effing-primitive-layout";
export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};
export default function Page({ resolvedUrl }) {
  const [valid, _valid] = useState(false);
  const [details, _details] = useState<ContactDetails | undefined>(undefined);
  const [helpDetails, setHelpDetails] = useState<HelpDetails | undefined>(
    undefined
  );

  const [ready, _ready] = useState(false);
  const [loading, _loading] = useState(false);
  const contact = useContact();

  const { testimonials } = useCMSTestimonials({
    per: 5,
  });

  const submit = useCallback(async () => {
    if (loading) {
      return;
    }

    _loading(true);

    try {
      const meta = [];

      if (details.show) {
        meta.push(["Show", details.show]);
      }
      if (details.opening_night) {
        meta.push(["Opening Night", details.opening_night]);
      }

      if (details.organization_type) {
        meta.push(["Organization Type", details.organization_type]);
      }

      await contact.send("contact", {
        email: details?.email,
        firstName: details?.name?.split(" ")?.[0],
        lastName:
          details?.name?.split(" ")?.[1] ||
          details?.name?.split(" ")?.[0] ||
          "",
        message: details?.message || "N/A",
        phone: details?.phone_number,
        organizationName: details?.organization,
        subject: "Contact Form Submission",
        meta,
      });
      Toast.success("Your message has been sent. We will be in touch soon.");
      _details(undefined);
      setTimeout(() => {
        window?.location?.reload?.();
      }, 2000);
      _loading(false);
    } catch (error) {
      Toast.error("Something went wrong. Please try again later.");
      _loading(false);
    }
  }, [contact, details, loading]);

  const [help, _help] = useState(false);
  const auth = useAuth();
  const business = useBusiness();
  const region = useRegion();
  const server = useServerRegion();
  const mobile = useMobile(1200);

  const _us = region.metas["🇺🇸 United States"];
  const _eu = region.metas["🇪🇺 Europe"];
  const _au = region.metas["🇦🇺 Australia"];
  const _g = region.metas["🌍 Global"];

  const _current: "us" | "au" | "uk" =
    (server?.meta?.code as "us" | "au" | "uk") || "us";

  useEffect(() => {
    setTimeout(() => {
      _ready(true);
    }, 200);
  }, []);

  if (!ready) {
    return null;
  }

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar={true}
      title="Contact Us | Broadway Media - Scenic Projections & Theatrical Resources"
      resolvedUrl={resolvedUrl}
    >
      <HelpFormModal
        open={help}
        close={() => _help(false)}
        submit={async () => {
          _help(false);

          const meta = [];

          if (helpDetails.show) {
            meta.push(["Show", details.show]);
          }
          if (helpDetails.performance_date) {
            meta.push(["Performance Date", helpDetails.performance_date]);
          }

          if (helpDetails.broadway_media_order_number) {
            meta.push([
              "Order Number",
              helpDetails.broadway_media_order_number,
            ]);
          }

          try {
            await contact.send("help", {
              email: helpDetails?.email,
              organizationName: helpDetails.name,
              firstName: helpDetails?.name?.split(" ")?.[0],
              lastName:
                helpDetails?.name?.split(" ")?.[1] ||
                helpDetails?.name?.split(" ")?.[0] ||
                "",
              message: helpDetails?.message || "N/A",
              phone: helpDetails?.phone_number,
              subject: "Help Form Submission",
              meta,
            });
            Toast.success(
              "Your message has been sent. We will be in touch soon."
            );
            setHelpDetails(undefined);
          } catch (error) {
            Toast.error("Something went wrong. Please try again later.");
          }
        }}
        change={(helpDetails) => setHelpDetails(helpDetails)}
      />

      <div
        style={{
          position: "relative",
          zIndex: "0",
        }}
      >
        <ReusableGradientHero
          slanted={false}
          span={mobile ? 24 : 12}
          src="/assets/hero/contact-us-background-image.jpg"
          backdrop="/assets/hero/contact-us-gradient-webp.webp"
        >
          <Rule parse="!_t d:block mb:24">
            Need assistance? {mobile && <br />} We are waiting in the wings!
          </Rule>

          <Box css="d:flex a:center">
            <DesignedButton
              properties={
                !mobile
                  ? { right: 10 }
                  : {
                      style: {
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }
              }
              container={
                !mobile
                  ? undefined
                  : {
                      style: {
                        width: "100%",
                        display: "block",
                      },
                    }
              }
              size="large"
              label="Schedule a Call"
              icon={
                <FiMessageCircle
                  size={20}
                  color="var(--primary)"
                  title="message circle icon"
                />
              }
              theme="tetriary"
              href="/contact/calendar"
            />

            {server.id === 1 ? (
              <DesignedButton
                label="Download Order Form"
                size="large"
                theme="tetriary"
                href={"/legal/documents/order-form"}
                properties={{ left: mobile ? 10 : 0 }}
              />
            ) : undefined}
          </Box>
        </ReusableGradientHero>

        <Box
          parse={
            mobile
              ? "p:relative w:100%"
              : "p:absolute w:40% h:80% b:0 r:52 i:1 d:flex a:center j:center pb:48"
          }
        >
          <TestimonialQuotes testimonials={testimonials} size="small" />
        </Box>
      </div>

      <ReusableCustomerServiceContainer />
      <ReusablePreSlantedContainer>
        <ReusableSection>
          <div className="container">
            <Row gutter={[36, 18]}>
              <Col xs={24} sm={24} md={8} lg={8}>
                <GridStepScroller
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                  gutter={[24, 16]}
                  items={[
                    <Box key={0} parse="w:100% p:relative br:10 oa:hidden">
                      <Box css="d:flex fd:column" parse="pa:20 c:?foreground">
                        <Rule parse="!ht d:block mb:24">
                          <a
                            rel="noreferrer"
                            href={`tel:${
                              (_current === "au"
                                ? _au
                                : _current === "uk"
                                ? _eu
                                : _us
                              ).phone
                            }`}
                          >
                            {
                              (_current === "au"
                                ? _au
                                : _current === "uk"
                                ? _eu
                                : _us
                              ).phone
                            }
                          </a>
                        </Rule>
                        {_current === "us" ? undefined : (
                          <Rule parse="!pl d:block mb:0">
                            <a
                              rel="noreferrer"
                              href={`mailto:${
                                (_current === "au" ? _au : _eu).email
                              }`}
                            >
                              {(_current === "au" ? _au : _eu).email}
                            </a>
                          </Rule>
                        )}
                        {_current === "us"
                          ? _us.addresses?.map((address, a) => (
                              <Box key={a} parse={"mb:16"}>
                                <Rule parse={"!ls d:block c:?font1 mb:4"}>
                                  {address.label}
                                </Rule>
                                <Rule parse={"!ps d:block c:?font4"}>
                                  {address.items.map((i, ii) => (
                                    <>
                                      {i}
                                      {ii !== address.items.length - 1 ? (
                                        <br />
                                      ) : undefined}
                                    </>
                                  ))}
                                </Rule>
                              </Box>
                            ))
                          : undefined}
                      </Box>
                    </Box>,
                    <Box key={0} parse="w:100% p:relative br:10 oa:hidden">
                      <Box css="d:flex fd:column" parse="pa:20 c:?foreground">
                        {_current === "us" ? undefined : (
                          <Box
                            parse="br:6 pa:6 mb:12"
                            color="rgba(255,255,255,0.05)"
                          >
                            <Rule parse="!pt d:block c:?font4">
                              Broadway Media operates with a distributed
                              workforce, serving our global customer base around
                              the clock. Please find the addresses for our
                              various entities listed below.
                            </Rule>
                          </Box>
                        )}
                        {_current === "us" ? undefined : (
                          <Box parse="mb:12">
                            <Rule parse="!ls d:block c:?font1 mb:4">
                              USA / Canada / Rest of World
                            </Rule>
                            <Rule parse="!ps d:block mb:4 c:?font3">
                              <a rel="noreferrer" href={`tel:${_g.email}`}>
                                {_g.email}
                              </a>
                            </Rule>
                            {_g.addresses?.map((address, a) => (
                              <Box key={a} parse="mb:4">
                                <Rule parse="!ps d:block c:?font4">
                                  {address.items.map((i, ii) => (
                                    <>
                                      {i}
                                      {ii !== address.items.length - 1 ? (
                                        <br />
                                      ) : undefined}
                                    </>
                                  ))}
                                </Rule>
                              </Box>
                            ))}
                          </Box>
                        )}
                        {_current === "uk" ? undefined : (
                          <Box parse="mb:12">
                            <Rule parse="!ls d:block c:?font1 mb:4">
                              United Kingdom / Europe
                            </Rule>
                            <Rule parse="!ps d:block mb:4 c:?font3">
                              <a rel="noreferrer" href={`tel:${_eu.email}`}>
                                {_eu.email}
                              </a>
                            </Rule>
                            {_eu.addresses?.map((address, a) => (
                              <Box key={a} parse="mb:4">
                                <Rule parse="!ps d:block c:?font4">
                                  {address.items.map((i, ii) => (
                                    <>
                                      {i}
                                      {ii !== address.items.length - 1 ? (
                                        <br />
                                      ) : undefined}
                                    </>
                                  ))}
                                </Rule>
                              </Box>
                            ))}
                          </Box>
                        )}
                        {_current === "au" ? undefined : (
                          <Box>
                            <Rule parse="!ls d:block c:?font1 mb:4">
                              Australia / New Zealand
                            </Rule>
                            <Rule parse="!ps d:block mb:4 c:?font3">
                              <a rel="noreferrer" href={`tel:${_au.email}`}>
                                {_au.email}
                              </a>
                            </Rule>
                            {_au.addresses?.map((address, a) => (
                              <Box key={a} parse="mb:4">
                                <Rule parse="!ps d:block c:?font4">
                                  {address.items.map((i, ii) => (
                                    <>
                                      {i}
                                      {ii !== address.items.length - 1 ? (
                                        <br />
                                      ) : undefined}
                                    </>
                                  ))}
                                </Rule>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>,
                    <ReusableDoubleGradientCard
                      properties={{ height: 180 }}
                      bottomless={true}
                      key={1}
                      press={() => _help(true)}
                      gradients={["#9231B4", "#9231B4"]}
                      holder={false}
                      bright={true}
                    >
                      <Box css="d:flex fd:column h:100% j:space-between">
                        <Box>
                          <Rule parse="!ll d:block mb:5 c:?font1">
                            Contact Support
                          </Rule>
                          <Rule parse="!ls d:block mb:24 c:?font3">
                            When you need help with technical support, our team
                            is ready
                          </Rule>
                        </Box>

                        <DesignedButton
                          size="large"
                          label="Submit Help Ticket"
                          icon={
                            <DesignedIcon
                              name="_ticket"
                              color="var(--primary)"
                            />
                          }
                          theme="tetriary"
                          press={() => _help(true)}
                        />
                      </Box>
                    </ReusableDoubleGradientCard>,
                  ]}
                />
              </Col>
              <Col xs={24} sm={24} md={16} lg={16}>
                <Rule parse="!hl d:block mb:36">Send us a message</Rule>
                <ContactForm
                  mobile
                  value={details}
                  initial={{
                    name: auth?.authState?.session?.user?.name,
                    email: auth?.authState?.session?.user?.email,
                    organization_type: business?.type?.name,
                    organization: auth?.authState?.session?.user?.organization,
                  }}
                  action={Formatter.gup("action")}
                  referrer={Formatter.gup("referrer")}
                  change={(details, valid) => {
                    _details(details);
                    _valid(valid);
                  }}
                />
                <Box parse="mt:24">
                  <DesignedButton
                    label="Send Message"
                    disable={!valid}
                    press={submit}
                    loading={loading}
                    properties={{
                      style: {
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      },
                    }}
                    container={{
                      style: {
                        width: "100%",
                        display: "flex",
                      },
                    }}
                  />
                </Box>
              </Col>
            </Row>
          </div>
        </ReusableSection>
      </ReusablePreSlantedContainer>
      {mobile ? undefined : <ReusableDownloadCallToAction />}
    </CatalogLayout>
  );
}
